import { Component, Element, Fragment, h, Host, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { getTeaserDate, getTeaserMonth, getTeaserTimeFormat, getTeaserYear, getValidDate } from "./helper/helper";
import {
  DateEventSchemeDefinition,
  DateEventSchemeName,
  DateLocalization,
  DateTypes
} from "./constants/date-event-types";
import {
  DATE_EVENT_SCHEME_CONFIG,
  DATE_EVENT_SCHEME_MAP,
  DATE_TYPE_ONE,
  DATE_TYPE_THREE,
  DATE_TYPE_TWO
} from "./constants/date-event-constants";
import { LocalizableComponent, Localization, setCurrentLocalizationDelegate } from "../../common/localizable-component";
import pfx from "../../utils/style-class-prefix-helper";
import { DATE_LOCALIZATION } from "./constants/date-event-i18n";
import { DateEventSchemeConfiguration } from "../../constants/colors";
// import { InitLogging } from "../../decorators/init-logging";

@Component({
  tag: "eon-ui-date-event",
  styleUrl: "date-event.scss",
  shadow: true
})
export class DateEvent
  implements
    ColorableComponent<DateEventSchemeConfiguration<DateEventSchemeDefinition>>,
    LocalizableComponent<DateLocalization> {
  // @InitLogging() sentryTransaction;
  schemeConfig: DateEventSchemeConfiguration<DateEventSchemeDefinition> = DATE_EVENT_SCHEME_CONFIG;
  schemeMap = DATE_EVENT_SCHEME_MAP;

  localization: Localization<DateLocalization> = DATE_LOCALIZATION;
  currentLocalization: DateLocalization;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: DateEventSchemeDefinition;
  @State() validDate: Date;
  @State() timeFormat: string;
  @State() teaserDate: number;
  @State() teaserMonth: string;
  @State() teaserYear: number;

  @Element() hostElement: HTMLEonUiDateEventElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: DateEventSchemeName;

  /**
   * This property can have date, accordingly the value will be displayed on Teaser
   */
  @Prop({ mutable: true }) date: string;

  /**
   * Property will hide and show the time format in date section
   */
  @Prop({ reflect: true, mutable: true }) hideTime: boolean = false;

  /**
   * timeZone property for uniform standard time, gives flexibility to authors to edit the timeZone
   * @example: CET/CEST
   */
  @Prop({ mutable: true }) timeZone: string;

  /**
   * twelveHourFormat depicts whether date should be of 12 hours format or 24 hours format
   */
  @Prop({ reflect: true, mutable: true }) twelveHourFormat: boolean = false;

  /**
   * Types of Date design format
   * @example: Type One | Type Two | Type Three
   */
  @Prop() dateEventType: DateTypes = DATE_TYPE_ONE;

  componentWillLoad() {
    this.setCurrentLocalization();
    this.applyScheme();
  }

  private getTimeFormat() {
    return getTeaserTimeFormat(
      getValidDate(this.date),
      this.currentLocalization && this.currentLocalization.locale,
      this.twelveHourFormat
    );
  }

  private getFinalDate() {
    return getTeaserDate(getValidDate(this.date));
  }

  private getFinalMonth() {
    return getTeaserMonth(getValidDate(this.date), this.currentLocalization && this.currentLocalization.locale);
  }

  private getFinalYear() {
    return getTeaserYear(getValidDate(this.date));
  }

  componentDidLoad() {
    this.setCssVariables();
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  dateContainer = () => {
    return (
      <div
        class={pfx({
          ["date"]: true
        })}
      >
        {this.getFinalDate()}
      </div>
    );
  };

  monthYearContainer = () => {
    return (
      <div class={pfx("month-year")}>
        <span class={pfx("month")}>{this.getFinalMonth()}</span>
        <span class={pfx("year")}>{this.getFinalYear()}</span>
      </div>
    );
  };

  timeContainer = () => {
    return (
      !this.hideTime && (
        <div class={pfx("time-zone-wrapper")}>
          {<span class={pfx("time")}>{this.getTimeFormat()}</span>}
          {this.timeZone && <span class={pfx("time-zone")}>{this.timeZone}</span>}
        </div>
      )
    );
  };

  private get typeOneTwoDateDesign() {
    return (
      <Fragment>
        {this.dateContainer()}
        <div class={pfx("month-time-wrapper")}>
          {this.monthYearContainer()}
          {this.timeContainer()}
        </div>
      </Fragment>
    );
  }

  private get typeThreeDateDesign() {
    return (
      <Fragment>
        <div class={pfx("date-month-wrapper")}>
          {this.dateContainer()}
          {this.monthYearContainer()}
        </div>
        {this.timeContainer()}
      </Fragment>
    );
  }

  render() {
    return (
      <Host>
        <div
          class={pfx({
            ["date-container"]: true,
            [`date-${this.dateEventType}`]: true
          })}
        >
          {(this.dateEventType === DATE_TYPE_ONE || this.dateEventType === DATE_TYPE_TWO) && this.typeOneTwoDateDesign}
          {this.dateEventType === DATE_TYPE_THREE && this.typeThreeDateDesign}
        </div>
      </Host>
    );
  }
}
